export interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Point2D {
  x: number;
  y: number;
}

export type Vec3 = [number, number, number];

export interface DetectedObjectBoundingBox {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
}

export interface FrameObject {
  detectedObjectId: string;
  bndbox: DetectedObjectBoundingBox;
}

export interface DocumentationFrame {
  frameName: string;
  rotation?: number[];
  translation?: number[];
  scale?: number;
  orientation?: number;
  objects: FrameObject[];
}

export type DocumentationStatus =
  | "created"
  | "in_process"
  | "in_validation"
  | "done";

export interface Documentation {
  _id: string;
  projectId: string;
  sectionId: string;
  videoPath?: string;
  status: DocumentationStatus;
  frames: DocumentationFrame[];
  createdAt: string;
  updatedAt: string;
  frameCount?: number;
  totalObjectCount?: number;
  processingDuration?: number | null;
  version?: number;
}

export interface PopulatedDocumentation extends Omit<
  Documentation,
  "projectId" | "sectionId"
> {
  sectionId: number;
}

export interface DocumentationPagination {
  current: number;
  total: number;
  count: number;
}

export interface DocumentationListResponse {
  documentation: PopulatedDocumentation[];
  pagination: DocumentationPagination;
}

export interface DocumentationStats {
  total: number;
  byStatus: Record<DocumentationStatus, number>;
}

export interface CreateDocumentationData {
  projectId: string;
  sectionId: string;
  videoPath?: string;
  status?: DocumentationStatus;
}

export interface UpdateDocumentationData {
  videoPath?: string;
  status?: DocumentationStatus;
}

export interface AddFrameData {
  frameName: string;
  objects?: FrameObject[];
  frameData?: {
    rotation?: number[];
    translation?: number[];
    scale?: number;
    orientation?: number;
  };
}

export interface UpdateFrameData {
  frameName?: string;
  objects?: FrameObject[];
  rotation?: number[];
  translation?: number[];
  scale?: number;
  orientation?: number;
}

export const DOCUMENTATION_STATUSES: DocumentationStatus[] = [
  "created",
  "in_process",
  "in_validation",
  "done",
];

export const DOCUMENTATION_STATUS_LABEL_KEYS: Record<
  DocumentationStatus,
  string
> = {
  created: "documentation.status_created",
  in_process: "documentation.status_in_process",
  in_validation: "documentation.status_in_validation",
  done: "documentation.status_done",
};

export const DOCUMENTATION_STATUS_LABELS: Record<DocumentationStatus, string> =
  {
    created: "Created",
    in_process: "In Process",
    in_validation: "In Validation",
    done: "Done",
  };

export const DOCUMENTATION_STATUS_COLORS: Record<DocumentationStatus, string> =
  {
    created: "bg-gray-100 text-gray-800",
    in_process: "bg-blue-100 text-blue-800",
    in_validation: "bg-yellow-100 text-yellow-800",
    done: "bg-green-100 text-green-800",
  };
